import { ReviewStatus } from '@skop-app/lib-types'
import type { OrganizationScopeKind } from '@/utils/organization'
import {
  OPTION_ARCHIVED_STATUS,
  OPTION_RETURNED_STATUS,
} from '@/services/property/option.status'

export type OptionFieldFilter = Record<string, unknown>

export type OptionFilterAudience = 'display' | 'picker' | 'moderation'

type BuildOptionFilterParams = {
  activeOrganizationId?: string
  organizationFamilyIds?: readonly string[]
  isAdmin?: boolean
  scopeKind?: OrganizationScopeKind
  userID?: string
  propertyOrganizationID?: string
}

const pushStatusClauses = ({
  bucket,
  status,
  userID,
  organizationIds,
}: {
  bucket: OptionFieldFilter[]
  status: string
  userID?: string
  organizationIds: string[]
}) => {
  if (userID) {
    bucket.push({
      status: { eq: status },
      userID: { eq: userID },
    })
  }

  organizationIds.forEach((organizationID) => {
    bucket.push({
      status: { eq: status },
      organizationID: { eq: organizationID },
    })
  })
}

const resolveScopedOrganizationIds = ({
  activeOrganizationId,
  organizationFamilyIds = [],
  propertyOrganizationID,
  isAdmin,
  scopeKind,
}: Omit<BuildOptionFilterParams, 'userID'>) => {
  const isOrganizationScope = scopeKind === 'organization'
  const scopedOrganizationIds = isOrganizationScope
    ? (organizationFamilyIds.length > 0
        ? organizationFamilyIds
        : [activeOrganizationId]
      ).filter((id): id is string => Boolean(id))
    : []
  const hasCustomDefinition =
    propertyOrganizationID &&
    propertyOrganizationID !== process.env.NEXT_PUBLIC_SKOP_ORGID &&
    !scopedOrganizationIds.includes(propertyOrganizationID)

  return [
    ...scopedOrganizationIds,
    ...(isAdmin && hasCustomDefinition ? [propertyOrganizationID] : []),
  ].filter((id): id is string => Boolean(id))
}

export function buildDisplayOptionFilter({
  activeOrganizationId,
  organizationFamilyIds,
  propertyOrganizationID,
  isAdmin,
  scopeKind,
  userID,
}: BuildOptionFilterParams): OptionFieldFilter {
  const organizationIds = [
    ...new Set(
      resolveScopedOrganizationIds({
        activeOrganizationId,
        organizationFamilyIds,
        propertyOrganizationID,
        isAdmin,
        scopeKind,
      }),
    ),
  ]

  const clauses: OptionFieldFilter[] = [
    { status: { eq: ReviewStatus.ACCEPTED } },
    { status: { eq: OPTION_ARCHIVED_STATUS } },
  ]

  pushStatusClauses({
    bucket: clauses,
    status: ReviewStatus.PENDING,
    userID,
    organizationIds,
  })

  pushStatusClauses({
    bucket: clauses,
    status: OPTION_RETURNED_STATUS,
    userID,
    organizationIds,
  })

  return {
    or: clauses,
  }
}

export function buildPickerOptionFilter({
  activeOrganizationId,
  organizationFamilyIds,
  propertyOrganizationID,
  isAdmin,
  scopeKind,
  userID,
}: BuildOptionFilterParams): OptionFieldFilter {
  const filter = buildDisplayOptionFilter({
    activeOrganizationId,
    organizationFamilyIds,
    propertyOrganizationID,
    isAdmin,
    scopeKind,
    userID,
  })
  return {
    ...filter,
    or: (filter.or as OptionFieldFilter[]).filter(
      (clause) =>
        (clause.status as { eq?: string })?.eq !== OPTION_ARCHIVED_STATUS,
    ),
  }
}

export function buildOptionFilter({
  activeOrganizationId,
  organizationFamilyIds,
  propertyOrganizationID,
  isAdmin,
  scopeKind,
  userID,
}: BuildOptionFilterParams): OptionFieldFilter {
  return buildDisplayOptionFilter({
    activeOrganizationId,
    organizationFamilyIds,
    propertyOrganizationID,
    isAdmin,
    scopeKind,
    userID,
  })
}

export function buildFieldFilter({
  organizationID,
}: {
  organizationID?: string
}): OptionFieldFilter {
  return {
    type: { eq: 'LIST' },
    or: [
      { organizationFieldsId: { eq: process.env.NEXT_PUBLIC_SKOP_ORGID } },
      ...(organizationID
        ? [{ organizationFieldsId: { eq: organizationID } }]
        : []),
    ],
  }
}
